import { History, RotateCcw, Sparkles } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { selectUser, useAuthStore } from '@/features/auth/auth.store';
import { useModulosDisponibles } from '@/features/modulos/hooks/use-modulos-disponibles';

import { asistenteApi } from '../asistente.api';
import { useAsistenteStore } from '../asistente.store';
import { AssistantStatusBadge } from '../components/assistant-status';
import { AlertList } from '../components/alert-list';
import { ChatComposer } from '../components/chat-composer';
import { ChatMessageBubble } from '../components/chat-message';
import { HistorialPanel } from '../components/historial-panel';
import { SuggestionList } from '../components/suggestion-list';
import { useAsistenteChat } from '../hooks/use-asistente-chat';
import { useRestaurarConversacion } from '../hooks/use-asistente-historial';
import { useAsistenteAlertas, useAsistenteStatus, useAsistenteSugerencias, useDescartarAlerta, useRestaurarAlertas } from '../hooks/use-asistente-meta';

/**
 * Pantalla del Asistente IA.
 *
 * Conversación en lenguaje natural sobre la operación de la estación. El modelo
 * sólo puede ejecutar herramientas de **lectura** en el backend; aquí únicamente
 * se dibuja lo que devuelve (`reply`, `cards`, `charts` y herramientas usadas).
 */
export function AsistentePage() {
  useDocumentTitle('Asistente IA');

  const user = useAuthStore(selectUser);
  const { data: catalogo } = useModulosDisponibles();

  const status = useAsistenteStatus();
  const sugerencias = useAsistenteSugerencias();
  const alertas = useAsistenteAlertas();
  const descartarAlerta = useDescartarAlerta();
  const restaurarAlertas = useRestaurarAlertas();

  const { messages, loading, send, reset, conversacionId } = useAsistenteChat({
    empresa: catalogo?.empresa?.razon_social ?? user?.company ?? null,
    sucursal: catalogo?.empresa?.sucursal ?? null,
  });

  const abrirConversacion = useAsistenteStore((state) => state.abrirConversacion);
  const conversacionTitulo = useAsistenteStore((state) => state.conversacionTitulo);
  const [historialAbierto, setHistorialAbierto] = useState(false);

  // Si el usuario tenía una conversación abierta, se recupera del servidor.
  const restaurando = useRestaurarConversacion();

  const endRef = useRef<HTMLDivElement>(null);

  const disponible = Boolean(status.data?.disponible) && !status.isError;
  const iniciado = messages.length > 0;

  // Mantiene la última respuesta a la vista.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, loading]);

  const handleSend = useCallback(
    (text: string) => {
      void send(text);
    },
    [send],
  );

  const handleSuggestion = useCallback(
    (texto: string) => {
      void send(texto);
    },
    [send],
  );

  /** Abre una conversación del historial en la pantalla. */
  const handleAbrirConversacion = useCallback(
    async (id: number) => {
      try {
        const conversacion = await asistenteApi.conversacion(id);
        abrirConversacion(conversacion);
      } catch {
        // Si falla, se mantiene lo que había en pantalla.
      }
    },
    [abrirConversacion],
  );

  return (
    <div className="flex h-[calc(100dvh-12.5rem)] min-h-[28rem] flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold tracking-tight text-ink-900">
            <span
              aria-hidden="true"
              className="flex size-8 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-accent-600 text-white"
            >
              <Sparkles className="size-4.5" />
            </span>
            Asistente IA
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Pregunta en lenguaje natural sobre ventas, inventario, compras, precios, metas,
            mantenimiento, créditos y gastos.
          </p>

          {conversacionTitulo ? (
            <p className="mt-1 truncate text-xs text-ink-400" title={conversacionTitulo}>
              Conversación: {conversacionTitulo}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <AssistantStatusBadge status={status.data} loading={status.isPending} isError={status.isError} />

          <Button
            variant="outline"
            size="sm"
            onClick={() => setHistorialAbierto(true)}
            leftIcon={<History className="size-4" />}
          >
            Historial
          </Button>

          {iniciado ? (
            <Button
              variant="outline"
              size="sm"
              onClick={reset}
              leftIcon={<RotateCcw className="size-4" />}
            >
              Nueva conversación
            </Button>
          ) : null}
        </div>
      </header>

      <HistorialPanel
        open={historialAbierto}
        onClose={() => setHistorialAbierto(false)}
        conversacionId={conversacionId}
        onAbrir={handleAbrirConversacion}
        onNueva={reset}
      />

      {!disponible && !status.isPending ? (
        <Alert variant="warning" title="El asistente no está disponible">
          {status.data?.motivo ?? 'No se pudo conectar con el servicio de IA. Intenta más tarde.'}
        </Alert>
      ) : null}

      {disponible ? (
        <AlertList
          alertas={alertas.data?.alertas ?? []}
          descartadas={alertas.data?.descartadas ?? 0}
          onSelect={handleSuggestion}
          onDescartar={(tipo) => descartarAlerta.mutate(tipo)}
          onRestaurar={() => restaurarAlertas.mutate()}
          disabled={loading}
        />
      ) : null}

      <Card className="mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col overflow-hidden">
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {restaurando ? (
            <p className="py-10 text-center text-sm text-ink-500">Recuperando tu conversación…</p>
          ) : null}

          {!iniciado && !restaurando ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 py-6 text-center">
              <span
                aria-hidden="true"
                className="flex size-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600 ring-1 ring-brand-100"
              >
                <Sparkles className="size-7" />
              </span>

              <div>
                <h2 className="text-base font-semibold text-ink-900">
                  ¿Qué quieres saber de la operación?
                </h2>
                <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-ink-500">
                  Consulto los datos reales del sistema en modo lectura. Prueba con una de estas
                  preguntas o escribe la tuya.
                </p>
              </div>

              <SuggestionList
                sugerencias={sugerencias.data ?? []}
                onSelect={handleSuggestion}
                disabled={!disponible}
                className="max-w-2xl"
              />
            </div>
          ) : (
            messages.map((message) => <ChatMessageBubble key={message.id} message={message} />)
          )}

          {loading ? (
            <div className="flex items-center gap-3" aria-live="polite">
              <span
                aria-hidden="true"
                className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-accent-600 text-white"
              >
                <Sparkles className="size-4" />
              </span>

              <div className="flex items-center gap-2 rounded-2xl rounded-bl-md border border-ink-100 bg-white px-4 py-3 shadow-soft">
                <span className="flex gap-1" aria-hidden="true">
                  {[0, 150, 300].map((delay) => (
                    <span
                      key={delay}
                      style={{ animationDelay: `${delay}ms` }}
                      className="size-1.5 animate-bounce rounded-full bg-brand-400"
                    />
                  ))}
                </span>
                <span className="text-sm text-ink-500">Analizando los datos…</span>
              </div>
            </div>
          ) : null}

          <div ref={endRef} />
        </div>

        <ChatComposer onSend={handleSend} loading={loading} disabled={!disponible} />
      </Card>
    </div>
  );
}
